import type { CelestialBodyDefinition } from "../types/celestialBody";
import { J2000_JD, AU_KM } from "../astronomy/constants";

/**
 * Elementos orbitais médios aproximados em J2000 (heliocêntricos eclípticos).
 * Fontes: NASA Planetary Fact Sheets / aproximação educacional Kepler.
 */
export const mercury: CelestialBodyDefinition = {
  id: "mercury",
  name: "Mercúrio",
  englishName: "Mercury",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 2439.7,
    massKg: 3.3011e23,
    surfaceGravityMS2: 3.7,
    escapeVelocityKmS: 4.25,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 0.387098 * AU_KM,
    eccentricity: 0.20563,
    inclinationDeg: 7.00487,
    longitudeAscendingNodeDeg: 48.331,
    argumentOfPeriapsisDeg: 29.124,
    meanAnomalyAtEpochDeg: 174.796,
    orbitalPeriodDays: 87.969,
  },
  rotation: {
    siderealRotationPeriodHours: 1407.6,
    axialTiltDeg: 0.034,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#A0A0A0",
  textureKey: "mercury",
  enabledByDefault: true,
  renderPriority: 1,
  gravitationalParameterKm3S2: 2.2032e4,
};

export const venus: CelestialBodyDefinition = {
  id: "venus",
  name: "Vênus",
  englishName: "Venus",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 6051.8,
    massKg: 4.8675e24,
    surfaceGravityMS2: 8.87,
    escapeVelocityKmS: 10.36,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 0.723332 * AU_KM,
    eccentricity: 0.006772,
    inclinationDeg: 3.39458,
    longitudeAscendingNodeDeg: 76.68,
    argumentOfPeriapsisDeg: 54.884,
    meanAnomalyAtEpochDeg: 50.115,
    orbitalPeriodDays: 224.701,
  },
  rotation: {
    siderealRotationPeriodHours: 5832.6,
    axialTiltDeg: 177.36,
    rotationDirection: "retrograde",
    epochJulianDate: J2000_JD,
  },
  color: "#E8CDA0",
  textureKey: "venus",
  enabledByDefault: true,
  renderPriority: 2,
  gravitationalParameterKm3S2: 3.24859e5,
};

export const earth: CelestialBodyDefinition = {
  id: "earth",
  name: "Terra",
  englishName: "Earth",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 6371.0,
    equatorialRadiusKm: 6378.1,
    polarRadiusKm: 6356.8,
    massKg: 5.97237e24,
    surfaceGravityMS2: 9.807,
    escapeVelocityKmS: 11.186,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 1.000001 * AU_KM,
    eccentricity: 0.0167086,
    inclinationDeg: 0.00005,
    longitudeAscendingNodeDeg: -11.26064,
    argumentOfPeriapsisDeg: 114.20783,
    meanAnomalyAtEpochDeg: 358.617,
    orbitalPeriodDays: 365.256,
  },
  rotation: {
    siderealRotationPeriodHours: 23.9345,
    axialTiltDeg: 23.439,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#2E6BFF",
  textureKey: "earth",
  enabledByDefault: true,
  renderPriority: 3,
  gravitationalParameterKm3S2: 3.986004418e5,
};

export const mars: CelestialBodyDefinition = {
  id: "mars",
  name: "Marte",
  englishName: "Mars",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 3389.5,
    massKg: 6.4171e23,
    surfaceGravityMS2: 3.71,
    escapeVelocityKmS: 5.03,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 1.523679 * AU_KM,
    eccentricity: 0.0934,
    inclinationDeg: 1.85,
    longitudeAscendingNodeDeg: 49.558,
    argumentOfPeriapsisDeg: 286.502,
    meanAnomalyAtEpochDeg: 19.373,
    orbitalPeriodDays: 686.98,
  },
  rotation: {
    siderealRotationPeriodHours: 24.6229,
    axialTiltDeg: 25.19,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#C1440E",
  textureKey: "mars",
  enabledByDefault: true,
  renderPriority: 4,
  gravitationalParameterKm3S2: 4.282837e4,
};

export const jupiter: CelestialBodyDefinition = {
  id: "jupiter",
  name: "Júpiter",
  englishName: "Jupiter",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 69_911,
    equatorialRadiusKm: 71_492,
    massKg: 1.8982e27,
    surfaceGravityMS2: 24.79,
    escapeVelocityKmS: 59.5,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 5.2044 * AU_KM,
    eccentricity: 0.0489,
    inclinationDeg: 1.303,
    longitudeAscendingNodeDeg: 100.464,
    argumentOfPeriapsisDeg: 273.867,
    meanAnomalyAtEpochDeg: 20.02,
    orbitalPeriodDays: 4332.59,
  },
  rotation: {
    siderealRotationPeriodHours: 9.925,
    axialTiltDeg: 3.13,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#C88B3A",
  textureKey: "jupiter",
  rings: [
    {
      innerRadiusKm: 92_000,
      outerRadiusKm: 226_000,
      inclinationDeg: 0.05,
      opacity: 0.15,
    },
  ],
  enabledByDefault: true,
  renderPriority: 5,
  gravitationalParameterKm3S2: 1.26686534e8,
};

export const saturn: CelestialBodyDefinition = {
  id: "saturn",
  name: "Saturno",
  englishName: "Saturn",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 58_232,
    equatorialRadiusKm: 60_268,
    massKg: 5.6834e26,
    surfaceGravityMS2: 10.44,
    escapeVelocityKmS: 35.5,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 9.5826 * AU_KM,
    eccentricity: 0.0565,
    inclinationDeg: 2.485,
    longitudeAscendingNodeDeg: 113.665,
    argumentOfPeriapsisDeg: 339.392,
    meanAnomalyAtEpochDeg: 317.02,
    orbitalPeriodDays: 10_759.22,
  },
  rotation: {
    siderealRotationPeriodHours: 10.656,
    axialTiltDeg: 26.73,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#E4D191",
  textureKey: "saturn",
  rings: [
    {
      innerRadiusKm: 74_500,
      outerRadiusKm: 140_220,
      inclinationDeg: 0,
      opacity: 0.85,
      textureKey: "saturn-rings",
    },
  ],
  enabledByDefault: true,
  renderPriority: 6,
  gravitationalParameterKm3S2: 3.7931187e7,
};

export const uranus: CelestialBodyDefinition = {
  id: "uranus",
  name: "Urano",
  englishName: "Uranus",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 25_362,
    massKg: 8.681e25,
    surfaceGravityMS2: 8.69,
    escapeVelocityKmS: 21.3,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 19.2184 * AU_KM,
    eccentricity: 0.046381,
    inclinationDeg: 0.773,
    longitudeAscendingNodeDeg: 74.006,
    argumentOfPeriapsisDeg: 96.998857,
    meanAnomalyAtEpochDeg: 142.2386,
    orbitalPeriodDays: 30_688.5,
  },
  rotation: {
    siderealRotationPeriodHours: 17.24,
    axialTiltDeg: 97.77,
    rotationDirection: "retrograde",
    epochJulianDate: J2000_JD,
  },
  color: "#7FDBFF",
  textureKey: "uranus",
  rings: [
    {
      innerRadiusKm: 41_800,
      outerRadiusKm: 51_100,
      inclinationDeg: 0,
      opacity: 0.35,
    },
  ],
  enabledByDefault: true,
  renderPriority: 7,
  gravitationalParameterKm3S2: 5.793939e6,
};

export const neptune: CelestialBodyDefinition = {
  id: "neptune",
  name: "Netuno",
  englishName: "Neptune",
  type: "planet",
  parentId: "sun",
  physical: {
    meanRadiusKm: 24_622,
    massKg: 1.02413e26,
    surfaceGravityMS2: 11.15,
    escapeVelocityKmS: 23.5,
  },
  orbit: {
    epochJulianDate: J2000_JD,
    semiMajorAxisKm: 30.11 * AU_KM,
    eccentricity: 0.009456,
    inclinationDeg: 1.767975,
    longitudeAscendingNodeDeg: 131.784,
    argumentOfPeriapsisDeg: 276.336,
    meanAnomalyAtEpochDeg: 256.228,
    orbitalPeriodDays: 60_182,
  },
  rotation: {
    siderealRotationPeriodHours: 16.11,
    axialTiltDeg: 28.32,
    rotationDirection: "prograde",
    epochJulianDate: J2000_JD,
  },
  color: "#4166F5",
  textureKey: "neptune",
  rings: [
    {
      innerRadiusKm: 41_900,
      outerRadiusKm: 62_900,
      inclinationDeg: 0,
      opacity: 0.25,
    },
  ],
  enabledByDefault: true,
  renderPriority: 8,
  gravitationalParameterKm3S2: 6.836529e6,
};

export const planets: CelestialBodyDefinition[] = [
  mercury,
  venus,
  earth,
  mars,
  jupiter,
  saturn,
  uranus,
  neptune,
];
